import { useState, useEffect, type FormEvent } from 'react';
import { authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import ConfirmModal from '../shared/ConfirmModal';
import GroupMembersModal from './GroupMembersModal';
import type { Group, ApiErrorBody } from '../../types';

export default function GroupsTab() {
  const [groupList, setGroupList] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [managingGroup, setManagingGroup] = useState<Group | null>(null);

  const [renamingGroupId, setRenamingGroupId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Group | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [togglingTaggingId, setTogglingTaggingId] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    apiFetch('/api/groups', { headers: authHeaders() })
      .then(r => r.json() as Promise<Group[]>)
      .then(data => setGroupList(data ?? []))
      .catch(() => setLoadError('Failed to load groups'))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch('/api/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: newGroupName.trim() }),
      });
      const data: ApiErrorBody & { data?: Group } = await res.json();
      if (!res.ok) { setSaveError(data.message ?? 'Failed to create group'); return; }
      setGroupList(prev => [...prev, data.data!].sort((a, b) => a.name.localeCompare(b.name)));
      setNewGroupName('');
      setShowForm(false);
    } catch {
      setSaveError('Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (e: FormEvent, groupId: number) => {
    e.preventDefault();
    setRenameSaving(true);
    setRenameError(null);
    try {
      const res = await apiFetch(`/api/groups/${groupId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: renameValue.trim() }),
      });
      const data: ApiErrorBody & { data?: Group } = await res.json();
      if (!res.ok) { setRenameError(data.message ?? 'Failed to rename group'); return; }
      setGroupList(prev =>
        prev.map(g => g.id === groupId ? { ...g, name: data.data!.name } : g)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setRenamingGroupId(null);
    } catch {
      setRenameError('Failed to rename group');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleToggleTagging = async (group: Group) => {
    setTogglingTaggingId(group.id);
    try {
      const res = await apiFetch(`/api/groups/${group.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ includeInTagging: !group.includeInTagging }),
      });
      if (!res.ok) return;
      setGroupList(prev =>
        prev.map(g => g.id === group.id ? { ...g, includeInTagging: !g.includeInTagging } : g)
      );
    } catch {
      // silently fail — state unchanged, user can retry
    } finally {
      setTogglingTaggingId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      const res = await apiFetch(`/api/groups/${pendingDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data: ApiErrorBody = await res.json();
        setDeleteError(data.message ?? 'Failed to delete group');
        return;
      }
      setGroupList(prev => prev.filter(g => g.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch {
      setDeleteError('Failed to delete group');
    }
  };

  const handleMemberCountChange = (groupId: number, delta: number) => {
    setGroupList(prev =>
      prev.map(g => g.id === groupId ? { ...g, memberCount: g.memberCount + delta } : g)
    );
  };

  return (
    <div>
      <div className='user-mgmt-header'>
        <div className='user-filter-tabs' />
        <button
          className='user-add-btn'
          onClick={() => { setShowForm(true); setSaveError(null); }}
        >
          + Add Group
        </button>
      </div>

      {showForm && (
        <form className='user-form' onSubmit={handleCreate}>
          <h3 className='user-form-title'>New Group</h3>
          <div className='user-form-row'>
            <div className='user-form-field'>
              <label>Group Name</label>
              <input
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>
          {saveError && <p className='user-form-error'>{saveError}</p>}
          <div className='user-form-actions'>
            <button
              type='button'
              className='user-cancel-btn'
              onClick={() => { setShowForm(false); setNewGroupName(''); setSaveError(null); }}
            >
              Cancel
            </button>
            <button type='submit' className='user-submit-btn' disabled={saving}>
              {saving ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      )}

      {loading && <p className='user-loading'>Loading groups...</p>}
      {loadError && <p className='user-error'>{loadError}</p>}
      {!loading && !loadError && groupList.length === 0 && (
        <p className='user-empty'>No groups yet.</p>
      )}

      {!loading && groupList.length > 0 && (
        <div className='table-responsive'>
          <table className='user-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Members</th>
                <th>Tagging</th>
                <th style={{ textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {groupList.map(g => (
                <tr key={g.id}>
                  <td>
                    {renamingGroupId === g.id ? (
                      <form className='group-rename-form' onSubmit={e => handleRename(e, g.id)}>
                        <input
                          className='group-rename-input'
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          autoFocus
                          required
                        />
                        <button type='submit' className='group-rename-save-btn' disabled={renameSaving}>
                          {renameSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type='button'
                          className='user-cancel-btn'
                          onClick={() => { setRenamingGroupId(null); setRenameError(null); }}
                        >
                          Cancel
                        </button>
                        {renameError && <span className='group-rename-error'>{renameError}</span>}
                      </form>
                    ) : (
                      g.name
                    )}
                  </td>
                  <td>{g.memberCount}</td>
                  <td>
                    <button
                      className={`group-tagging-badge${g.includeInTagging ? ' group-tagging-badge--on' : ' group-tagging-badge--off'}`}
                      onClick={() => handleToggleTagging(g)}
                      disabled={togglingTaggingId === g.id}
                      aria-label={`${g.includeInTagging ? 'Remove' : 'Include'} ${g.name} in tagging scope`}
                    >
                      {g.includeInTagging ? 'Included' : 'Excluded'}
                    </button>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div className='group-actions-cell'>
                      <button className='user-edit-btn' onClick={() => setManagingGroup(g)}>
                        Manage
                      </button>
                      <button
                        className='user-edit-btn'
                        onClick={() => { setRenamingGroupId(g.id); setRenameValue(g.name); setRenameError(null); }}
                      >
                        Rename
                      </button>
                      <button
                        className='user-deactivate-btn'
                        onClick={() => { setPendingDelete(g); setDeleteError(null); }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {managingGroup && (
        <GroupMembersModal
          group={managingGroup}
          onClose={() => setManagingGroup(null)}
          onMemberCountChange={handleMemberCountChange}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title='Delete Group'
          body={
            <>
              <strong>{pendingDelete.name}</strong> and all its members will be permanently deleted.
              {deleteError && (
                <p className='user-form-error' style={{ marginTop: '12px', marginBottom: 0 }}>
                  {deleteError}
                </p>
              )}
            </>
          }
          confirmLabel='Delete'
          isDestructive
          onConfirm={handleDelete}
          onCancel={() => { setPendingDelete(null); setDeleteError(null); }}
        />
      )}
    </div>
  );
}
